<template>
  <InfuseModal :title="t('e_connector.chargingStationLinks.modal.create.title')" @close="emit('close')">
    <form ref="form" id="create-link" @submit.prevent="onSave()">
      <div class="flex max-h-[475px] w-[600px] flex-col gap-4 overflow-y-auto px-5">
        <div class="flex items-start gap-4">
          <InfuseField
            :label="t('e_connector.common.chargingStationLink.centralSystem')"
            class="basis-1/2"
            size="small"
          >
            <InfuseSelect
              name="centralSystem"
              size="small"
              v-model="centralSystemId"
              :options="centralSystemOptions"
              :label="t('e_connector.chargingStationLinks.modal.field.centralSystem.placeholder')"
              :required="true"
            />
          </InfuseField>

          <InfuseField :label="t('e_connector.common.chargingStationLink.type')" class="basis-1/2" size="small">
            <template v-if="!mayCreateMaster" #label>
              <InfuseTooltip
                :content="t('e_connector.chargingStationLinks.modal.field.type.tooltip')"
                class="inline cursor-help text-yellow-500"
              >
                <font-awesome-icon :icon="['fal', 'circle-exclamation']" />
              </InfuseTooltip>
            </template>

            <InfuseSelect
              name="type"
              size="small"
              v-model="type"
              :options="typeOptions"
              :required="true"
              :disabled="!mayCreateMaster"
            />
          </InfuseField>
        </div>

        <template v-if="isDefiningNew">
          <div class="flex items-start gap-4">
            <InfuseField :label="t('e_connector.common.centralSystem.name')" class="basis-full" size="small">
              <InfuseInput
                size="small"
                v-model="newName"
                :maxlength="255"
                :placeholder="t('e_connector.centralSystems.modal.field.name.placeholder')"
                :required="true"
              />
            </InfuseField>
          </div>

          <div class="flex items-start gap-4">
            <InfuseField :label="t('e_connector.common.centralSystem.url')" class="basis-full" size="small">
              <InfuseInput
                type="url"
                size="small"
                v-model="newUri"
                :maxlength="255"
                :placeholder="t('e_connector.centralSystems.modal.field.uri.placeholder')"
                :required="true"
              />
            </InfuseField>
          </div>

          <div class="flex items-start gap-4">
            <InfuseField :label="t('e_connector.common.centralSystem.ocppVersion')" class="basis-1/2" size="small">
              <InfuseSelect
                name="ocppVersion"
                size="small"
                v-model="newOcppVersion"
                :options="ocppVersionOptions"
                :label="t('e_connector.centralSystems.modal.field.ocppVersion.placeholder')"
                :required="true"
              />
            </InfuseField>
          </div>
        </template>

        <div class="flex items-start gap-4">
          <InfuseField
            :label="t('e_connector.common.chargingStationLink.customChargingStationName')"
            class="basis-full"
            size="small"
            optional
          >
            <InfuseInput
              size="small"
              v-model="customChargingStationName"
              :maxlength="255"
              :placeholder="t('e_connector.chargingStationLinks.modal.field.customChargingStationName.placeholder')"
            />
          </InfuseField>
        </div>

        <div class="flex items-start gap-4">
          <InfuseField
            :label="t('e_connector.common.chargingStationLink.commandsAllowed')"
            class="basis-1/3"
            size="small"
          >
            <InfuseToggle
              id="commandsAllowed"
              :value="commandsAllowed"
              @on="commandsAllowed = true"
              @off="commandsAllowed = false"
            />
          </InfuseField>

          <InfuseField
            :label="t('e_connector.common.chargingStationLink.authorizationAllowed')"
            class="basis-1/3"
            size="small"
          >
            <InfuseToggle
              id="authorizationAllowed"
              :value="authorizationAllowed"
              @on="authorizationAllowed = true"
              @off="authorizationAllowed = false"
            />
          </InfuseField>

          <InfuseField
            :label="t('e_connector.common.chargingStationLink.forwardBasicAuth')"
            class="basis-1/3"
            size="small"
          >
            <InfuseToggle
              id="forwardBasicAuth"
              :value="forwardBasicAuth"
              @on="forwardBasicAuth = true"
              @off="forwardBasicAuth = false"
            />
          </InfuseField>
        </div>
      </div>
    </form>

    <template #actions>
      <InfuseButton color="secondary" @click="emit('close')">
        {{ t('e_connector.common.action.cancel') }}
      </InfuseButton>
      <InfuseButton type="submit" form="create-link" :disabled="loading" :loading="loading">
        {{ t('e_connector.common.action.create') }}
      </InfuseButton>
    </template>
  </InfuseModal>
</template>

<script setup lang="ts">
import {
  InfuseButton,
  InfuseField,
  InfuseInput,
  InfuseModal,
  InfuseSelect,
  InfuseToggle,
  InfuseTooltip
} from '@infuse/ui/components';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { CentralSystem, LinkedCentralSystem, LinkedCentralSystemType, OcppVersion } from '@infuse/e-connector/central-system/domain';
import { ChargingStation } from '@infuse/e-connector/charging-station/domain';
import { centralSystemPermissions } from '@infuse/api/e-connector/central-system';
import { useCurrentUserStore } from '@infuse/iam/user/store';

const NEW_CENTRAL_SYSTEM_VALUE = '__new__';

export interface NewCentralSystemDefinition {
  name: string;
  uri: string;
  ocppVersion: OcppVersion;
}

interface Props {
  loading: boolean;
  centralSystems: CentralSystem[];
  chargingStation: ChargingStation;
}

interface Emits {
  (e: 'close'): void;
  (e: 'create', linkedCentralSystem: LinkedCentralSystem, newCentralSystem?: NewCentralSystemDefinition): void;
}

const props = withDefaults(defineProps<Props>(), {
  loading: false
});

const emit = defineEmits<Emits>();

const { t } = useI18n();
const userStore = useCurrentUserStore();

const canCreateCentralSystem = computed(
  () => !userStore.enabled || userStore.hasPermission(centralSystemPermissions.CREATE)
);

const existingOptions = computed(() =>
  props.centralSystems
    .filter((centralSystem) =>
      props.chargingStation.linkedCentralSystems.every((lcs) => lcs.centralSystemId !== centralSystem.id)
    )
    .map((centralSystem) => ({
      key: centralSystem.name,
      value: centralSystem.id
    }))
);

const centralSystemOptions = computed(() => [
  ...existingOptions.value,
  ...(canCreateCentralSystem.value
    ? [{ key: t('e_connector.chargingStationLinks.modal.field.centralSystem.defineNew'), value: NEW_CENTRAL_SYSTEM_VALUE }]
    : [])
]);

const typeOptions = Object.values(LinkedCentralSystemType);
const ocppVersionOptions = Object.values(OcppVersion);

const centralSystemId = ref<number | string>(
  existingOptions.value[0]?.value ?? (canCreateCentralSystem.value ? NEW_CENTRAL_SYSTEM_VALUE : 0)
);
const customChargingStationName = ref<string>('');
const commandsAllowed = ref<boolean>(false);
const authorizationAllowed = ref<boolean>(false);
const forwardBasicAuth = ref<boolean>(false);

const newName = ref<string>('');
const newUri = ref<string>('');
const newOcppVersion = ref<OcppVersion>(OcppVersion.V16);

const isDefiningNew = computed(() => centralSystemId.value === NEW_CENTRAL_SYSTEM_VALUE);

watch(newUri, (uri) => {
  if (!newName.value) {
    try {
      newName.value = new URL(uri).hostname;
    } catch {
      // not a valid URL yet
    }
  }
});

const mayCreateMaster = computed(
  () =>
    !props.chargingStation.linkedCentralSystems.some(
      (linkedCentralSystem) => linkedCentralSystem.type === LinkedCentralSystemType.MASTER
    )
);

const type = ref<LinkedCentralSystemType>(
  mayCreateMaster.value ? LinkedCentralSystemType.MASTER : LinkedCentralSystemType.READONLY
);

function onSave() {
  if (isDefiningNew.value) {
    const linkedCentralSystem = {
      centralSystemId: 0,
      chargingStationId: props.chargingStation.id,
      type: type.value,
      customChargingStationName: customChargingStationName.value,
      commandsAllowed: commandsAllowed.value,
      sessionAuthorizationAllowed: authorizationAllowed.value,
      forwardBasicAuth: forwardBasicAuth.value
    } as LinkedCentralSystem;

    emit('create', linkedCentralSystem, {
      name: newName.value,
      uri: newUri.value,
      ocppVersion: newOcppVersion.value
    });
  } else {
    const centralSystem = props.centralSystems.find((cs) => cs.id === Number(centralSystemId.value));

    const linkedCentralSystem = {
      centralSystemId: centralSystem?.id,
      chargingStationId: props.chargingStation.id,
      type: type.value,
      customChargingStationName: customChargingStationName.value,
      commandsAllowed: commandsAllowed.value,
      sessionAuthorizationAllowed: authorizationAllowed.value,
      forwardBasicAuth: forwardBasicAuth.value
    } as LinkedCentralSystem;

    emit('create', linkedCentralSystem);
  }
}
</script>
